import { useState } from 'react';
import { css } from '@emotion/react';
import { FaxQueries } from '@frontend/api-fax';
import { FaxDraftQueries } from '@frontend/api-fax-draft';
import { FaxMediaQueries } from '@frontend/api-fax-media';
import { NoAccessEmptyState, StartNewFaxSVG, UploadCoverSheetSVG } from '@frontend/assets';
import { EmptyStates } from '@frontend/components';
import { StartNewFax, UploadCoversheet } from '@frontend/fax-assets#svg';
import { HttpError } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { Page } from '@frontend/page';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { FaxPrefixes } from '@frontend/tracking-prefixes';
import { useSearch, useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import {
  SpinningLoader,
  Text,
  TextLink,
  useFileUpload,
  useAlert,
  Button,
  SearchField,
  useFormField,
  useDebouncedValue,
  NakedButton,
} from '@frontend/design-system';
import { DraftCreationCard } from '../components/draft-coversheet/draft-creation-card';
import { TemplateList } from '../components/templates/template-list';
import { defaultHTML } from '../constants';

export const NewFaxPage = () => {
  const { t } = useTranslation('fax');
  const navigate = useNavigate();
  const { navigate: settingsNavigate } = useSettingsNavigate();
  const alert = useAlert();
  const isMobile = useMatchMedia({ maxWidth: breakpoints.mobile.max });
  const isXSmall = useMatchMedia({ maxWidth: breakpoints.xsmall.max });
  const [isUploading, setIsUploading] = useState(false);
  const [isCreateFaxLoading, setIsCreateFaxLoading] = useState(false);

  const { selectedLocationId, contactId } = useSearch({ from: '/fax/drafts/new', shouldThrow: false }) ?? {};

  const searchFieldProps = useFormField({
    type: 'text',
    value: '',
  });

  const debouncedSearch = useDebouncedValue(searchFieldProps.value.trim());

  const { selectedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const faxTemplatesFlag = getFeatureFlagValue('fax-templates');
  const locationId = selectedLocationId || selectedLocationIds[0];
  const {
    data: faxNumber,
    isFetched: isGetFaxNumberFetched,
    isLoading: isGetFaxNumberLoading,
    isError: isGetFaxNumberError,
  } = FaxQueries.useQueryDefaultFaxNumber(locationId);

  const {
    data,
    isLoading: isGetTemplatesLoading,
    isFetching: isGetTemplatesFetching,
    isError: isGetTemplatesError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = FaxMediaQueries.useInfiniteListCoversheetTemplate(locationId, [locationId], debouncedSearch, {
    enabled: faxTemplatesFlag,
  });

  const templates = data ? data.pages.flatMap((page) => page.coversheetTemplates) : [];

  const invalidateDraftList = FaxDraftQueries.useInvalidateDraftList();

  const { mutateAsync: createDraft } = FaxDraftQueries.useStartDraft({
    onSuccess: () => {
      invalidateDraftList();
    },
    onError: (err) => {
      const error = err as HttpError;
      if (error.message.toLowerCase().includes('invalid fax number')) {
        alert.error(t('Invalid fax number. Please reach out to support to set this up.'));
      } else {
        alert.error(t('An unknown error occurred'));
      }
    },
  });
  const { mutateAsync: setHTML } = FaxDraftQueries.useUpdateCoversheetHtml({});
  const { mutateAsync: addRecipient } = FaxDraftQueries.useAddRecipient({});
  const { mutate: setCoversheetUploadedFile } = FaxDraftQueries.useSetCoversheetUploadedFile({
    onSuccess: (res) => {
      navigate({
        to: '/fax/drafts/$draftId/upload',
        params: { draftId: res.id },
        search: {
          selectedLocationId,
        },
      });
    },
    onSettled: () => {
      setIsUploading(false);
    },
  });

  const { mutateAsync: upload } = FaxDraftQueries.useUploadMedia({});

  const showMissingPhoneMessage = () => {
    alert.error(
      t('Your location does not have a fax number to send from. Please reach out to support to set this up.')
    );
  };

  const {
    inputProps,
    handleOnClick: openFileDialog,
    resetFiles,
  } = useFileUpload({
    acceptedFileType: ['image'],
    onFileUpload: (files) => {
      if (!faxNumber) {
        return showMissingPhoneMessage();
      }
      createDraft({
        faxPhone: faxNumber,
        locationId,
      }).then((draft) => {
        setIsUploading(true);
        if (contactId) {
          addRecipient({ draftId: draft.id, locationId, contactId });
        }
        upload([[files[0]], locationId])
          .then((res) => {
            if (res[0]?.mediaId) {
              setCoversheetUploadedFile({ draftId: draft.id, locationId, mediaId: res[0]?.mediaId });
            } else {
              setIsUploading(true);
            }
            resetFiles();
          })
          .catch((err) => {
            alert.error(err instanceof HttpError ? err.message : t('An unknown error occurred'));
            resetFiles();
            setIsUploading(false);
          });
      });
    },
  });

  const createNewFaxWithCoversheet = () => {
    if (!faxNumber) {
      return showMissingPhoneMessage();
    }
    setIsCreateFaxLoading(true);
    createDraft({
      faxPhone: faxNumber,
      locationId,
    })
      .then((res) => {
        if (contactId) {
          addRecipient({ draftId: res.id, locationId, contactId });
        }
        setHTML({
          draftId: res.id,
          coversheetHtml: `<html><body>${defaultHTML}</body></html>`,
          locationId: res.locationId,
        })
          .then((res) => {
            navigate({
              to: '/fax/drafts/$draftId/cover',
              params: { draftId: res.id },
              search: { selectedLocationId },
            });
          })
          .finally(() => {
            setIsCreateFaxLoading(false);
          });
      })
      .finally(() => {
        setIsCreateFaxLoading(false);
      });
  };

  const createNewFaxWithoutCoverSheet = () => {
    if (!faxNumber) {
      return showMissingPhoneMessage();
    }
    createDraft({
      faxPhone: faxNumber,
      locationId,
    }).then((res) => {
      if (contactId) {
        addRecipient({ draftId: res.id, locationId, contactId });
      }
      navigate({
        to: '/fax/drafts/$draftId/upload',
        params: { draftId: res.id },
        search: { selectedLocationId },
      });
    });
  };

  const createNewFaxFromUpload = () => {
    openFileDialog();
  };

  const getNextPage = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <Page
      css={{
        '.page-header': {
          marginBottom: theme.spacing(3),
          backgroundColor: theme.colors.white,
        },
      }}
    >
      <Page.Header>
        <Page.Header.Breadcrumbs
          breadcrumbs={[
            { label: t('Fax Inbox'), to: '/fax/inbox' },
            { label: faxTemplatesFlag ? t('Select Cover Sheet') : t('Select a Cover Sheet Type') },
          ]}
        />
        <Page.Header.Heading>
          <Page.Header.Title title={faxTemplatesFlag ? t('Select Cover Sheet') : t('Select a Cover Sheet Type')} />
          <Page.Header.Subtitle
            subtitle={
              !faxTemplatesFlag && !!faxNumber
                ? t(
                    'Every good fax has a cover sheet. Select which kind of cover sheet you would like to proceed with to build your new fax message.'
                  )
                : null
            }
          />
          <Page.Header.Action>
            {faxNumber ? (
              <Button
                variant='secondary'
                onClick={createNewFaxWithoutCoverSheet}
                trackingId={`${FaxPrefixes.New}-skip-cover-sheet`}
              >
                {t('Skip Cover Sheet')}
              </Button>
            ) : null}
          </Page.Header.Action>
        </Page.Header.Heading>
      </Page.Header>
      <Page.Body css={{ display: 'flex', flexDirection: 'column' }}>
        {isGetFaxNumberLoading ? (
          <SpinningLoader
            css={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          />
        ) : isGetFaxNumberError ? (
          <EmptyStates
            emptyStateConfig={{
              type: 'fax',
              header: t('Something went wrong. Please try again.'),
            }}
          />
        ) : faxNumber && isGetFaxNumberFetched ? (
          faxTemplatesFlag ? (
            <>
              <div
                css={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: isMobile && !isXSmall ? 'flex-start' : 'center',
                  flexDirection: isMobile && !isXSmall ? 'column-reverse' : 'row',
                  rowGap: isMobile && !isXSmall ? theme.spacing(2) : 0,
                  columnGap: theme.spacing(2),
                  marginBottom: theme.spacing(3),
                  paddingBottom: theme.spacing(3),
                  borderBottom: `1px solid ${theme.colors.neutral20}`,
                }}
              >
                <NakedButton
                  css={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    columnGap: theme.spacing(2),
                    border: `1px solid ${theme.colors.neutral20}`,
                    borderRadius: theme.borderRadius.medium,
                    padding: theme.spacing(1),
                    width: 350,
                    '&:hover': {
                      border: `1px solid ${theme.colors.primary50}`,
                    },
                  }}
                  onClick={createNewFaxWithCoversheet}
                >
                  <StartNewFax
                    css={{ maxWidth: 70 }}
                    assetsFallbackProps={{
                      fallbackHeight: '65px',
                      fallbackWidth: '70px',
                      size: 'medium',
                    }}
                  />
                  <div
                    css={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      flexDirection: 'column',
                    }}
                  >
                    <Text weight='bold' size='large'>
                      {isCreateFaxLoading ? t('Creating New Fax...') : isXSmall ? t('New') : t('Start New')}
                    </Text>
                    {!isXSmall &&
                      (isCreateFaxLoading ? (
                        <SpinningLoader css={{ margin: 'auto' }} size='medium' />
                      ) : (
                        <Text color='subdued' size='small'>
                          {t('Create a new fax cover sheet')}
                        </Text>
                      ))}
                  </div>
                </NakedButton>
                <NakedButton
                  css={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    columnGap: theme.spacing(2),
                    border: `1px solid ${theme.colors.neutral20}`,
                    borderRadius: theme.borderRadius.medium,
                    padding: theme.spacing(1),
                    width: 350,
                    '&:hover': {
                      border: `1px solid ${theme.colors.primary50}`,
                    },
                  }}
                  onClick={() => createNewFaxFromUpload()}
                  disabled={isUploading}
                >
                  <UploadCoversheet
                    css={{ maxWidth: 70 }}
                    assetsFallbackProps={{
                      fallbackHeight: '65px',
                      fallbackWidth: '70px',
                      size: 'medium',
                    }}
                  />
                  <div
                    css={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      flexDirection: 'column',
                    }}
                  >
                    <Text weight='bold' size='large' textAlign='left'>
                      {isUploading ? t('Uploading Cover Sheet...') : isXSmall ? t('Upload') : t('Upload Cover Sheet')}
                    </Text>
                    {!isXSmall &&
                      (isUploading ? (
                        <SpinningLoader css={{ margin: 'auto' }} size='medium' />
                      ) : (
                        <Text color='subdued' size='small' textAlign='left'>
                          {t('Use another document as your fax cover')}
                        </Text>
                      ))}
                  </div>
                </NakedButton>
              </div>
              <div
                css={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  marginBottom: theme.spacing(2),
                  columnGap: theme.spacing(1),
                }}
              >
                <SearchField
                  {...searchFieldProps}
                  name='templates-search'
                  css={{
                    flex: isXSmall ? '1 1 auto' : isMobile ? '1 1 50%' : undefined,
                    minWidth: isMobile && !isXSmall ? '50%' : undefined,
                  }}
                />
                <Button
                  iconName='plus'
                  variant='secondary'
                  size='large'
                  css={{ flexShrink: 0 }}
                  onClick={() => {
                    navigate({
                      to: '/fax/drafts/template-editor',
                      search: {
                        selectedLocationId: locationId,
                      },
                    });
                  }}
                  data-trackingid={`create-fax-template`}
                >
                  {isXSmall ? null : t('Create Template')}
                </Button>
              </div>
              <TemplateList
                locationId={locationId}
                isError={isGetTemplatesError}
                templates={templates}
                isLoading={isGetTemplatesLoading}
                isFetching={isGetTemplatesFetching || isFetchingNextPage}
                disableTemplates={isUploading || isCreateFaxLoading}
                contactId={contactId}
                trackingId={`use-fax-template`}
                getNextPage={getNextPage}
              />
            </>
          ) : (
            <ul
              css={css`
                display: flex;
                margin: 0;
                padding: 0;
                flex-wrap: wrap;
              `}
            >
              <DraftCreationCard
                title={t('Start New')}
                description={t('Create a brand new cover sheet with the fax builder.')}
                buttonLabel={t('Start Cover Sheet')}
                color={theme.colors.success5}
                onSelect={() => createNewFaxWithCoversheet()}
                imageSrc={StartNewFaxSVG}
                trackingId={`${FaxPrefixes.New}-start-new`}
              />
              <DraftCreationCard
                title={t('Upload Cover Sheet')}
                description={
                  isUploading ? (
                    <SpinningLoader />
                  ) : (
                    t('Want to upload your own cover sheet? Jump right into uploading your fax documents.')
                  )
                }
                buttonLabel={isUploading ? t('Uploading...') : t('Upload My Own')}
                buttonDisabled={isUploading}
                color={theme.colors.primary10}
                onSelect={isUploading ? () => {} : () => createNewFaxFromUpload()}
                imageSrc={UploadCoverSheetSVG}
                trackingId={`${FaxPrefixes.New}-upload-cover-sheet`}
              />
            </ul>
          )
        ) : (
          <NoAccessEmptyState
            message={
              <>
                {t(
                  'No fax numbers are available for this location — none may be set up, or all are privately assigned to other users. Manage access in '
                )}{' '}
                <TextLink onClick={() => settingsNavigate({ to: '/organization/phone-numbers' })}>
                  {t('Phone Numbers and Porting')}
                </TextLink>
              </>
            }
          />
        )}
        <input type='file' id='file-drop' hidden {...inputProps} />
      </Page.Body>
    </Page>
  );
};
